import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { formatHours } from '../monthly/format';
import { parseLocalDate } from '../../utils/dateHelpers';
import { GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';

const PAGE_SIZE = 100;
const DAY_BG = 'var(--rt-hover)';
const num = (n) => Number(n || 0).toLocaleString('en-GB');
export const hoursLabel = (minutes) => Number(formatHours(minutes)).toLocaleString('en-GB', { maximumFractionDigits: 2 });
const dayLabel = (key) => parseLocalDate(key).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });

const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}`, verticalAlign: 'top' };
const numSx = { ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function Ticket({ value }) {
  if (!value) return null;
  return (
    <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, bgcolor: 'var(--rt-track)', color: INK, px: 0.75, py: 0.1, borderRadius: 1, whiteSpace: 'nowrap' }}>
      {value}
    </Box>
  );
}

/**
 * Worklog raggruppati per giorno, con totali del giorno e del periodo (minuti e ore).
 * scope: 'mine' (azioni di riga) | 'projects' (selezione per il cambio ticket) | 'people'.
 */
export default function WorklogTable({ rows, scope, selectable, selectedIds, onToggleRow, onToggleAll, onEdit, onDelete, lockReason, maxHeight }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows]);

  const showPerson = scope !== 'mine';
  const showActions = scope === 'mine';
  const colCount = 4 + (selectable ? 1 : 0) + (showPerson ? 1 : 0) + (showActions ? 1 : 0);

  const { dayTotals, totalMinutes } = useMemo(() => {
    const totals = {};
    let sum = 0;
    rows.forEach((w) => {
      const t = totals[w.dateKey] || { count: 0, minutes: 0 };
      t.count += 1;
      t.minutes += Number(w.effort) || 0;
      totals[w.dateKey] = t;
      sum += Number(w.effort) || 0;
    });
    return { dayTotals: totals, totalMinutes: sum };
  }, [rows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const selectedCount = selectable ? rows.filter((w) => selectedIds.has(w.id)).length : 0;

  const body = [];
  pageRows.forEach((w, idx) => {
    if (idx === 0 || pageRows[idx - 1].dateKey !== w.dateKey) {
      const t = dayTotals[w.dateKey];
      body.push(
        <TableRow key={`day-${w.dateKey}`}>
          <TableCell colSpan={colCount - 2 - (showActions ? 1 : 0)} sx={{ ...cellSx, bgcolor: DAY_BG, py: 0.75 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
              {dayLabel(w.dateKey)}
            </Typography>
            <Typography component="span" sx={{ fontSize: 12.5, color: MUTED, ml: 1 }}>
              {t.count} worklog{t.count === 1 ? '' : 's'}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...numSx, bgcolor: DAY_BG, py: 0.75, fontWeight: 700 }}>{num(t.minutes)}</TableCell>
          <TableCell sx={{ ...numSx, bgcolor: DAY_BG, py: 0.75, fontWeight: 700 }}>{hoursLabel(t.minutes)}</TableCell>
          {showActions && <TableCell sx={{ ...cellSx, bgcolor: DAY_BG, py: 0.75 }} />}
        </TableRow>,
      );
    }
    const selected = selectable && selectedIds.has(w.id);
    const locked = showActions ? lockReason(w) : '';
    body.push(
      <TableRow key={w.id} hover selected={selected} sx={{ '&.Mui-selected, &.Mui-selected:hover': { bgcolor: GREEN_SOFT } }}>
        {selectable && (
          <TableCell sx={{ ...cellSx, py: 0.25, px: 0.5 }}>
            <Checkbox size="small" checked={selected} onChange={() => onToggleRow(w.id)} color="success" inputProps={{ 'aria-label': 'Select worklog' }} />
          </TableCell>
        )}
        {showPerson && (
          <TableCell sx={cellSx}>
            <Typography noWrap sx={{ fontSize: 13, color: INK }} title={w.user_name}>
              {w.user_name}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, color: MUTED }} title={scope === 'people' ? `People Manager: ${w.people_manager_name || '—'}` : undefined}>
              {w.team_name || (scope === 'people' ? w.people_manager_name : '') || '—'}
            </Typography>
          </TableCell>
        )}
        <TableCell sx={{ ...cellSx, maxWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, color: INK, minWidth: 0 }} title={w.project_name}>
              {w.project_name}
            </Typography>
            <Ticket value={String(w.ticket_id || '').trim()} />
          </Box>
          <Typography noWrap sx={{ fontSize: 12, color: MUTED }} title={w.description || ''}>
            {w.description || '—'}
          </Typography>
        </TableCell>
        <TableCell sx={cellSx}>
          <Typography noWrap sx={{ fontSize: 13, color: INK }} title={w.activity_name}>
            {w.activity_name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
            {w.effort_type_name}
          </Typography>
        </TableCell>
        <TableCell sx={numSx}>{num(w.effort)}</TableCell>
        <TableCell sx={numSx}>{formatHours(w.effort)}</TableCell>
        {showActions && (
          <TableCell sx={{ ...cellSx, py: 0.25, whiteSpace: 'nowrap', textAlign: 'right' }}>
            {locked ? (
              <Tooltip title={locked}>
                <LockIcon sx={{ fontSize: 18, color: 'var(--rt-faint)', mt: 0.75, mr: 1 }} />
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(w)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => onDelete(w)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </TableCell>
        )}
      </TableRow>,
    );
  });

  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: maxHeight ?? 'calc(100vh - 330px)', minHeight: 200 }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: showPerson ? 860 : 720 }}>
          <colgroup>
            {selectable && <col style={{ width: 44 }} />}
            {showPerson && <col style={{ width: 200 }} />}
            <col />
            <col style={{ width: 190 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 84 }} />
            {showActions && <col style={{ width: 88 }} />}
          </colgroup>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell sx={{ ...headSx, py: 0.25, px: 0.5 }}>
                  <Tooltip title="Select all the worklogs shown, then change their ticket">
                    <Checkbox
                      size="small"
                      color="success"
                      checked={rows.length > 0 && selectedCount === rows.length}
                      indeterminate={selectedCount > 0 && selectedCount < rows.length}
                      onChange={(e) => onToggleAll(e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all worklogs' }}
                    />
                  </Tooltip>
                </TableCell>
              )}
              {showPerson && <TableCell sx={headSx}>Person</TableCell>}
              <TableCell sx={headSx}>Project · ticket · description</TableCell>
              <TableCell sx={headSx}>Activity · type</TableCell>
              <TableCell sx={{ ...headSx, textAlign: 'right' }}>Effort (min)</TableCell>
              <TableCell sx={{ ...headSx, textAlign: 'right' }}>Effort (h)</TableCell>
              {showActions && <TableCell sx={headSx} />}
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          Total{' '}
          <Box component="span" sx={{ fontWeight: 700, color: INK }}>
            {num(totalMinutes)} min · {hoursLabel(totalMinutes)} h
          </Box>{' '}
          in {num(rows.length)} worklog{rows.length === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {rows.length ? `${num(page * PAGE_SIZE + 1)}–${num(Math.min(rows.length, (page + 1) * PAGE_SIZE))} of ${num(rows.length)}` : '0 of 0'}
          </Typography>
          <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
            <ChevronRightIcon fontSize="small" sx={{ color: page >= pageCount - 1 ? undefined : GREEN }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
